import { useEffect, useRef, useState } from 'react'
import mermaid from 'mermaid'
import type { Diagram } from '../types'

interface DiagramViewerProps {
  diagrams: Diagram[]
  format: 'native' | 'mermaid' | 'plantuml' | 'c4plantuml' | 'dot'
}

// Initialize mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
})

export default function DiagramViewer({ diagrams, format }: DiagramViewerProps) {
  const [selectedDiagram, setSelectedDiagram] = useState(0)
  const [renderedSvg, setRenderedSvg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (diagrams.length === 0) return

    const diagram = diagrams[selectedDiagram]
    if (!diagram) return

    if (format === 'mermaid') {
      renderMermaid(diagram.definition)
    } else {
      // For PlantUML/DOT, show the code (could integrate with Kroki for rendering)
      setRenderedSvg(null)
      setError(null)
    }
  }, [diagrams, selectedDiagram, format])

  async function renderMermaid(definition: string) {
    try {
      setError(null)
      const { svg } = await mermaid.render('diagram-' + Date.now(), definition)
      setRenderedSvg(svg)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to render diagram')
      setRenderedSvg(null)
    }
  }

  if (diagrams.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        No diagrams to display. Parse your DSL first.
      </div>
    )
  }

  const currentDiagram = diagrams[selectedDiagram]

  return (
    <div className="h-full flex flex-col">
      {/* Diagram tabs */}
      <div className="flex border-b border-gray-200 overflow-x-auto">
        {diagrams.map((diagram, index) => (
          <button
            key={diagram.key}
            onClick={() => setSelectedDiagram(index)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
              selectedDiagram === index
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {diagram.title || diagram.key}
          </button>
        ))}
      </div>

      {/* Diagram content */}
      <div className="flex-1 overflow-auto p-4" ref={containerRef}>
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg mb-4">
            {error}
          </div>
        )}

        {format === 'mermaid' && renderedSvg ? (
          <div
            className="flex justify-center"
            dangerouslySetInnerHTML={{ __html: renderedSvg }}
          />
        ) : (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-500 uppercase">{format}</span>
              <button
                onClick={() => navigator.clipboard.writeText(currentDiagram.definition)}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Copy to clipboard
              </button>
            </div>
            <pre className="text-sm overflow-x-auto whitespace-pre-wrap">
              {currentDiagram.definition}
            </pre>
          </div>
        )}
      </div>
    </div>
  )
}
